import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getInstructorForCourse, getInstructorCategory, type Instructor, type InstructorCategory } from "@/lib/instructorMapping";
import { launchItemRoute, normalizeLaunchModuleKey } from "@/lib/launchPath";
import type { AnyValue } from "@/lib/rowTypes";

export interface NextLessonInfo {
  /** Underlying launch path item type. */
  itemType: "lesson" | "simulator";
  /** Lesson UUID for lessons, module key (e.g. "budget_builder") for simulators. */
  refId: string;
  /** Pre-resolved navigation route — banner + Launch Path card must agree on this. */
  route: string;
  lessonId: string;
  lessonName: string;
  courseName: string;
  lessonNumber: number;
  totalLessons: number;
  /** Position within the curated Launch Path (1-indexed across all items). */
  launchPosition: number;
  /** Total number of active Launch Path items (lessons + modules). */
  launchTotal: number;
  /** Position within the curated Launch Path's lesson items only (1-indexed). */
  launchLessonPosition: number;
  /** Total number of lesson items in the active Launch Path. */
  launchLessonTotal: number;
  instructor: Instructor;
  category: InstructorCategory;
}

/**
 * useNextLesson — the dashboard hero's "Next Up" lesson.
 *
 * Source of truth: the admin-curated Launch Path (launch_path_items + learner
 * completion). This guarantees the hero banner and the Launch Path card
 * always agree on which lesson is "next". We do NOT fall back to a generic
 * curriculum walk — if the Launch Path has no eligible lesson item left, we
 * return null and the banner shows its empty/explore state.
 */
export function useNextLesson() {
  const { user } = useAuth();
  const [data, setData] = useState<NextLessonInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const client = supabase;

        // 1) Load the FULL active, ordered Launch Path. The hero banner must
        //    surface whatever is next — lesson OR module — so it stays in
        //    lockstep with the Launch Path card.
        const { data: pathRows, error: pathErr } = await client
          .from("launch_path_items")
          .select("id, position, item_type, ref_id, title, subtitle")
          .eq("is_active", true)
          .order("position", { ascending: true });
        if (pathErr) throw pathErr;

        const rows = (pathRows || []) as Array<{ id: string; position: number; item_type: "lesson" | "simulator"; ref_id: string; title: string; subtitle: string | null }>;
        const lessonRefs = rows.filter((r) => r.item_type === "lesson").map((r) => r.ref_id);
        const simRefs = rows
          .filter((r) => r.item_type === "simulator")
          .map((r) => normalizeLaunchModuleKey(r.ref_id))
          .filter(Boolean);
        if (!rows.length) {
          if (!cancelled) {
            setData(null);
            setLoading(false);
          }
          return;
        }

        // 2) Resolve referenced lessons + learner completion (lessons + modules) in parallel.
        const [lessonRowsRes, progressRes, moduleRes] = await Promise.all([
          lessonRefs.length
            ? supabase
                .from("lessons")
                .select("lesson_id, lesson_name, course_name, lesson_order")
                .in("lesson_id", lessonRefs)
            : Promise.resolve({ data: [] }),
          lessonRefs.length
            ? supabase
                .from("user_progress")
                .select("lesson_id")
                .eq("user_id", user.id)
                .eq("completed", true)
                .in("lesson_id", lessonRefs)
            : Promise.resolve({ data: [] }),
          simRefs.length
            ? client
                .from("module_completions")
                .select("module_key")
                .eq("user_id", user.id)
                .eq("passed", true)
                .in("module_key", simRefs)
            : Promise.resolve({ data: [] }),
        ]);

        const lessonById = new Map<string, { lesson_id: string; lesson_name: string; course_name: string; lesson_order: number }>();
        (lessonRowsRes.data || []).forEach((l: AnyValue) => lessonById.set(l.lesson_id, l));
        const completed = new Set((progressRes.data || []).map((p: AnyValue) => p.lesson_id));
        const completedSims = new Set((moduleRes.data || []).map((p: AnyValue) => p.module_key));

        // 3) Walk the path in order and pick the first incomplete item —
        //    lesson OR module. This is what guarantees the hero banner and
        //    the Launch Path card agree on "what's next".
        let pickRow: typeof rows[number] | null = null;
        for (const row of rows) {
          if (row.item_type === "lesson") {
            const lesson = lessonById.get(row.ref_id);
            if (!lesson) {
              console.warn(
                `[useNextLesson] Launch Path item ${row.id} (position ${row.position}) references missing lesson ${row.ref_id}; skipping.`,
              );
              continue;
            }
            if (completed.has(lesson.lesson_id)) continue;
          } else {
            const moduleKey = normalizeLaunchModuleKey(row.ref_id);
            if (moduleKey && completedSims.has(moduleKey)) continue;
          }
          pickRow = row;
          break;
        }

        if (!pickRow) {
          if (!cancelled) {
            setData(null);
            setLoading(false);
          }
          return;
        }

        if (pickRow.item_type === "lesson") {
          const lesson = lessonById.get(pickRow.ref_id)!;
          const { data: courseLessons } = await supabase
            .from("lessons")
            .select("lesson_id, lesson_order")
            .eq("course_name", lesson.course_name)
            .order("lesson_order");
          const ordered = (courseLessons || []).sort(
            (a: AnyValue, b: AnyValue) => a.lesson_order - b.lesson_order,
          );
          const idx = ordered.findIndex((l: AnyValue) => l.lesson_id === lesson.lesson_id);
          const lessonRows = rows.filter((r) => r.item_type === "lesson");
          const launchLessonIdx = lessonRows.findIndex((r) => r.ref_id === pickRow!.ref_id);
          if (!cancelled) {
            setData({
              itemType: "lesson",
              refId: lesson.lesson_id,
              route: launchItemRoute("lesson", lesson.lesson_id),
              lessonId: lesson.lesson_id,
              lessonName: lesson.lesson_name,
              courseName: lesson.course_name,
              lessonNumber: idx >= 0 ? idx + 1 : 1,
              totalLessons: ordered.length || 1,
              launchPosition: pickRow.position,
              launchTotal: rows.length,
              launchLessonPosition: launchLessonIdx >= 0 ? launchLessonIdx + 1 : 1,
              launchLessonTotal: lessonRows.length || 1,
              instructor: getInstructorForCourse(lesson.course_name),
              category: getInstructorCategory(lesson.course_name),
            });
            setLoading(false);
          }
        } else {
          // Module item — surface launch_path_items.title/subtitle directly.
          const lessonRowsCount = rows.filter((r) => r.item_type === "lesson").length;
          if (!cancelled) {
            setData({
              itemType: "simulator",
              refId: pickRow.ref_id,
              route: launchItemRoute("simulator", pickRow.ref_id),
              lessonId: pickRow.ref_id,
              lessonName: pickRow.title,
              courseName: pickRow.subtitle ?? "HeirWise Module",
              lessonNumber: pickRow.position,
              totalLessons: 13,
              launchPosition: pickRow.position,
              launchTotal: rows.length,
              launchLessonPosition: pickRow.position,
              launchLessonTotal: lessonRowsCount || 1,
              instructor: getInstructorForCourse("Financial Foundations"),
              category: "finance",
            });
            setLoading(false);
          }
        }
      } catch (err) {
        console.error("useNextLesson error:", err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return { data, loading };
}